import { EEntityType, ESceneMode, IEntityInfo } from '@/editor/types';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { IPoint3d } from '@/editor/math/IPoint3d';
import { globalStores } from '@/editor/stores';
import { getRotatedRectangleVertices } from '@/editor/math/utils';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { FloatingPoint } from '@/editor/math/number';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getPathFromEdges } from '@/editor/utils/edge';
import { sampleShapePath, toPlainPoint } from '@/editor/utils/wallShape/sampleShape';
import { getSeparateWallOutline } from '@/editor/data/separateWall/utils';
import { isMultiSelectDoorWindowOnly } from '@/editor/utils/select/multi-select';
import type { IFaceData } from '@/editor/data/face/face';
import type { IWindowData } from '@/editor/data/model/model';
import type { WallData } from '@/editor/data/wall/Wall';
import type { SeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import type { RegionData } from '@/editor/data/region/Region';
import type { RoomData } from '@/editor/data/room/Room';
import type { IMultiModelData } from '@/editor/data/multiModel/multiModel';
import { EMenuQuadrant, IAnchorCorner } from './anchorTypes';

export type { IAnchorCorner };
export { EMenuQuadrant };

interface IResolveAnchorCornersParams {
  sceneMode: ESceneMode;
  entityInfo: IEntityInfo;
}

const QUADRANT_PROBES: ReadonlyArray<{ quadrant: EMenuQuadrant; signX: number; signY: number }> = [
  { quadrant: EMenuQuadrant.rightTop, signX: 1, signY: 1 },
  { quadrant: EMenuQuadrant.rightBottom, signX: 1, signY: -1 },
  { quadrant: EMenuQuadrant.leftTop, signX: -1, signY: 1 },
  { quadrant: EMenuQuadrant.leftBottom, signX: -1, signY: -1 }
];

/**
 * 按实体类型产出对象菜单的候选角点。不判断角点是否在屏幕上——净可视区要扣掉
 * 左右面板，那是应用层的信息，筛选与排序在 UI 侧完成。
 */
export function resolveAnchorCorners({ sceneMode, entityInfo }: IResolveAnchorCornersParams): IAnchorCorner[] {
  return getEntityCorners(sceneMode, entityInfo).filter((corner) => isValidPoint(corner.point));
}

function isValidPoint(point: IPoint3d): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z);
}

function getEntityCorners(sceneMode: ESceneMode, { entityId, type }: IEntityInfo): IAnchorCorner[] {
  const is3d = sceneMode === ESceneMode.scene3d;

  switch (type) {
    case EEntityType.MultiModel: {
      const data = globalStores.getDataByIdType('', type);
      return data ? getMultiModelCorners(data, is3d) : [];
    }
    case EEntityType.DoorWindow: {
      const data = globalStores.getDataByIdType(entityId, type);
      return data ? toCorners(getDoorWindowFootprint(data), heightsOf(is3d, data.offGround, data.height)) : [];
    }
    case EEntityType.Furniture:
    case EEntityType.Assembly: {
      const data = globalStores.getDataByIdType(entityId, type);
      if (!data) return [];
      const footprint = getRotatedRectangleVertices(data.width, data.thickness, data.center, data.rotation);
      return toCorners(footprint, heightsOf(is3d, data.offGround, data.height));
    }
    case EEntityType.Column: {
      const data = globalStores.getDataByIdType(entityId, type);
      if (!data) return [];
      // 圆柱没有角点，退化成外接正方形的四角
      const footprint =
        'radius' in data
          ? getRotatedRectangleVertices(data.radius * 2, data.radius * 2, data.center, 0)
          : getRotatedRectangleVertices(data.width, data.thickness, data.center, data.rotation);
      return toCorners(footprint, heightsOf(is3d, data.offGround, data.height));
    }
    case EEntityType.Wall: {
      const data = globalStores.getDataByIdType(entityId, type);
      return data
        ? toCorners(getWallFootprint(data), heightsOf(is3d, data.baseData.offGround, data.baseData.height))
        : [];
    }
    case EEntityType.SeparateWall: {
      const data = globalStores.getDataByIdType(entityId, type);
      return data ? toCorners(getSeparateWallFootprint(data), heightsOf(is3d, data.offGround, data.height)) : [];
    }
    case EEntityType.Room:
    case EEntityType.Region: {
      const data = globalStores.getDataByIdType(entityId, type);
      return data ? getOutlineCorners(data, is3d) : [];
    }
    case EEntityType.Face: {
      const data = globalStores.getDataByIdType(entityId, type);
      return data ? getFaceCorners(data) : [];
    }
    default:
      return [];
  }
}

/** 2D 的角点一律贴地；3D 展开成底面与顶面两层 */
function heightsOf(is3d: boolean, offGround: number, height: number): number[] {
  if (!is3d) return [0];
  return FloatingPoint.equal(height, 0) ? [offGround] : [offGround, offGround + height];
}

function toCorners(footprint: IPoint2d[], heights: number[]): IAnchorCorner[] {
  const corners: IAnchorCorner[] = [];
  for (const z of heights) {
    for (const point of footprint) {
      corners.push({ point: { x: point.x, y: point.y, z }, blockedQuadrants: [] });
    }
  }
  return corners;
}

/**
 * 门窗只取它埋在墙里的那块矩形。完整包围盒会随开启图形翻转而变化，锚点会跟着跳。
 */
function getDoorWindowFootprint(data: IWindowData): IPoint2d[] {
  return getRotatedRectangleVertices(data.width, data.thickness, data.center, data.rotation);
}

/**
 * 墙只取两端端面的四个真角点。弧墙在 drawShape 里已被离散成大量采样点，那些不是角点。
 */
function getWallFootprint(data: WallData): IPoint2d[] {
  const { startShape, endShape, baseShape } = data.drawWallInfo;
  const endCaps = [...startShape, ...endShape];
  return endCaps.length ? endCaps.map(toPlainPoint) : sampleShapePath(baseShape);
}

function getSeparateWallFootprint(data: SeparateWallData): IPoint2d[] {
  const { startShape, endShape } = data.drawSeparateWallInfo;
  const endCaps = [...startShape, ...endShape];
  // 零厚度的虚拟隔墙没有端面，getSeparateWallOutline 给的是中心线两端
  return endCaps.length ? endCaps.map(toPlainPoint) : getSeparateWallOutline(data).points;
}

/**
 * 房间与分区取轮廓顶点。2D 额外算出每个顶点被房间内部占住的象限，UI 据此跳过会压住房间的摆法；
 * 3D 视角是斜的，象限失去意义，一律不产出。
 */
function getOutlineCorners(data: RoomData | RegionData, is3d: boolean): IAnchorCorner[] {
  const path = getPathFromEdges(data.pathInfos);
  const vertices = path.map(toPlainPoint);
  if (!vertices.length) return [];

  if (is3d) return toCorners(vertices, heightsOf(true, data.offGround, data.height));

  const outline = sampleShapePath(path);
  return vertices.map((vertex, index) => ({
    point: { x: vertex.x, y: vertex.y, z: 0 },
    blockedQuadrants: getBlockedQuadrants(
      vertex,
      vertices[(index - 1 + vertices.length) % vertices.length],
      vertices[(index + 1) % vertices.length],
      outline
    )
  }));
}

/**
 * 探针距离取相邻两条边较短者的四分之一，跟着轮廓尺寸走，避免写死毫米数在小房间里踩到边外。
 */
function getBlockedQuadrants(
  vertex: IPoint2d,
  previous: IPoint2d,
  next: IPoint2d,
  outline: IPoint2d[]
): EMenuQuadrant[] {
  if (outline.length < 3) return [];

  const step = Math.min(Point2dUtils.distanceTo(vertex, previous), Point2dUtils.distanceTo(vertex, next)) / 4;
  if (!FloatingPoint.greaterThan(step, 0)) return [];

  return QUADRANT_PROBES.filter(({ signX, signY }) =>
    Curve2dUtils.isPointInPolygon({ x: vertex.x + signX * step, y: vertex.y + signY * step }, outline)
  ).map(({ quadrant }) => quadrant);
}

/**
 * 墙面按 baseData 的形态分派，五种形态没有共同的判别字段，用 `in` 收窄。
 * 墙面只在 3D 有菜单，高度一律按 3D 展开。
 */
function getFaceCorners(data: IFaceData): IAnchorCorner[] {
  const { baseData } = data;

  if ('contour' in baseData) {
    return toCorners(sampleShapePath(baseData.contour), [baseData.offGround]);
  }

  const heights = heightsOf(true, baseData.offGround, baseData.height);

  if ('radius' in baseData) {
    return toCorners(
      getRotatedRectangleVertices(baseData.radius * 2, baseData.radius * 2, baseData.center, 0),
      heights
    );
  }

  // 弧墙面同样只取两个端点，不取弧上的采样点
  if ('start' in baseData) {
    return toCorners([toPlainPoint(baseData.start), toPlainPoint(baseData.end)], heights);
  }

  return toCorners(
    getRotatedRectangleVertices(baseData.width, baseData.thickness, baseData.center, baseData.rotation),
    heights
  );
}

/**
 * 多选整体是轴对齐包围盒（getMultiModelBoxInfo 把 rotation 钉成 0）。
 * 纯门窗多选没有整体盒，改取各门窗墙内矩形角点的并集。
 */
function getMultiModelCorners(data: IMultiModelData, is3d: boolean): IAnchorCorner[] {
  if (isMultiSelectDoorWindowOnly(data)) {
    return data.modelInfos.flatMap((info) =>
      toCorners(getDoorWindowFootprint(info.data), heightsOf(is3d, info.data.offGround, info.data.height))
    );
  }

  const footprint = getRotatedRectangleVertices(data.width, data.thickness, data.center, 0);
  return toCorners(footprint, heightsOf(is3d, data.offGround, data.height));
}
